<template>
    <div class="row justify-content-center">
        <CCard v-if="ready && activeOrder">
            <CCardHeader>
                <p>Payment Overview</p>
                <CRow>
                    <CCol col="6" class="text-left">
                        <h5>
                            Order Amount
                        </h5>
                    </CCol>
                    <CCol col="6" class="text-right">
                        <h5 class="total">
                            {{ activeOrder.subtotal | currency }}
                        </h5>
                    </CCol>
                </CRow>
            </CCardHeader>
            <CCardBody>
                <CRow>
                    <CCol col="6" class="text-left">
                        <p>
                            Subtotal
                        </p>
                    </CCol>
                    <CCol col="6" class="text-right">
                        <p class="total">
                            {{ activeOrder.subtotal | currency }}
                        </p>
                    </CCol>
                </CRow>
                <CRow>
                    <CCol col="6" class="text-left">
                        <p>
                            Tax
                        </p>
                    </CCol>
                    <CCol col="6" class="text-right">
                        <p class="total">
                            {{ activeOrder.tax | currency }}
                        </p>
                    </CCol>
                </CRow>
                <CRow>
                    <CCol col="6" class="text-left">
                        <p>
                            Shipping
                        </p>
                    </CCol>
                    <CCol col="6" class="text-right">
                        <p class="total">
                            {{ activeOrder.shipping | currency }}
                        </p>
                    </CCol>
                </CRow>
                <CRow>
                    <CCol col="6" class="text-left">
                        <p>
                            Administration Fee
                        </p>
                    </CCol>
                    <CCol col="6" class="text-right">
                        <p class="total">
                            {{ activeOrder.administrationFee | currency }}
                        </p>
                    </CCol>
                </CRow>
                <CRow>
                    <CCol col="6" class="text-left">
                        <p>
                            Total
                        </p>
                    </CCol>
                    <CCol col="6" class="text-right">
                        <p class="total">
                            {{ activeOrder.totalWithAdmin | currency }}
                        </p>
                    </CCol>
                </CRow>
            </CCardBody>
            <CCardFooter>
                <h4 class="text-center">
                    {{ activeOrder.totalWithAdmin | currency }}
                </h4>
                <div class="text-right">
                    <label><input id="saveCard" type="checkbox" v-model="doNotSaveCard" @change="toggleSaveCardHandler"> Don't Save My Card</label>
                </div>
                <div id="dropin-container"></div>
                <CButton
                    color="success"
                    class="btn-block px-4  mb-1"
                    @click="payNow"
                    type="submit"
                    :disabled="paymentInstance == null"
                    ><CIcon name="cil-credit-card" />
                    Pay Now
                </CButton>
                <p>
                    Payments made for this order will be authorized now, but not
                    charged until shipped
                </p>
            </CCardFooter>
        </CCard>
    </div>
</template>

<script>
/**
 * Payment totals confirmation page
 * @author Mithun Paulose (Fresche Solutions)
 * @date July 2020
 */

const PAYMENT_TYPE_CART = "C";

// JavaScript save card var that Braintree dropin JS can access. We'll keep it in sync
// with our Vue JS var, doNotSaveCard, that's synced to the checkbox.
// Prefixed with "js" as an indicator that it's a native JavaScript var.
var jsDoNotSaveCard = false;

import { mapState, mapMutations, mapActions, mapGetters } from "vuex";

import { mapFields } from "vuex-map-fields";
export default {
    name: "PayNow",
    computed: {
        ...mapFields("Order", ["activeOrder"])
    },
    data() {
        return {
            feePercent: 0,
            ready: false,
            paymentInstance: null,
            doNotSaveCard: false,
        };
    },
    async mounted() {
        this.init();
    },
    methods: {
        ...mapActions("BrainTree", ["fetchAuthorization", "postPaymentMethod"]),
        ...mapGetters("BrainTree", ["getAuthorization"]),
        ...mapActions("Invoice", ["fetchAdministrationFee"]),
        ...mapGetters("Invoice", ["getAdministrationFee"]),
        ...mapActions("User", ["fetchSelectedCustomerInfo"]),
        ...mapGetters("User", ["getSelectedCustomerInfo"]),
        async init() {

            // Initiallise the native JS save card var that Braintree dropin JS can access
            // to our Vue JS var that we'll keep synced to the checkbox.
            jsDoNotSaveCard = this.doNotSaveCard;

            if (this.activeOrder) {

                // get the current administration fee percentage
                let customerInfo = this.getSelectedCustomerInfo();
                if (!customerInfo) {
                    await this.fetchSelectedCustomerInfo();
                    customerInfo = this.getSelectedCustomerInfo();
                }

                let feePercent = customerInfo.adminFee;

                // calculate admin fee
                this.activeOrder.administrationFee =
                    (parseFloat(this.activeOrder.total) *
                        parseFloat(feePercent)) /
                    100;
                this.activeOrder.administrationFee =
                    Math.round(this.activeOrder.administrationFee * 100) / 100;

                // add admin fee to the total
                this.activeOrder.totalWithAdmin =
                    parseFloat(this.activeOrder.total) +
                    this.activeOrder.administrationFee;

                this.setupPaymentForm();

                this.ready = true;
            }
        },

        // Setup the Payment Form
        async setupPaymentForm() {

            let vm = this;
            let addEditCardsClickEvents = this.addEditCardsClickEvents;

            // get the brain tree authorization token for this user
            let authorization = this.getAuthorization();
            if (!authorization) {
                authorization = await this.fetchAuthorization();
            }

            // create the payment form instance
            braintree.dropin.create(
                {
                    authorization: authorization,
                    container: "#dropin-container",
                    vaultManager: true,
                    isGuestCheckout: jsDoNotSaveCard
                },
                function(createErr, instance) {
                    vm.paymentInstance = instance;
                    vm.paymentInstance._model.isGuestCheckout = jsDoNotSaveCard;
                    addEditCardsClickEvents();
                }
            );

            // create the data collector instance
            // this is used to provide additional fraud detection
            braintree.client.create(
                {
                    authorization: authorization
                },
                function(err, clientInstance) {
                    braintree.dataCollector.create(
                        {
                            client: clientInstance,
                            paypal: true
                        },
                        function(error, dataCollectorInstance) {
                            if (error) {
                                return;
                            }
                            // At this point, you should access the dataCollectorInstance.deviceData value and provide it
                            vm.deviceData = dataCollectorInstance.deviceData;
                        }
                    );
                }
            );
        },

        payNow() {
            let vm = this;

            if (vm.paymentInstance) {
                // request authorization from BrainTree on payment method
                vm.paymentInstance.requestPaymentMethod(
                    vm.requestPaymentMethod
                );
            }
        },

        // Payment Method Callback
        async requestPaymentMethod(requestPaymentMethodErr, payload) {
            if (requestPaymentMethodErr) {
                return;
            }

            this.activeOrder.type = PAYMENT_TYPE_CART;
            this.activeOrder.cartId = this.activeOrder.id;

            // get the payment information and send to server
            this.activeOrder.payment_method_nonce = payload.nonce;
            this.activeOrder.deviceData = this.deviceData;

            // post to brain tree
            let paymentResponse = await this.postPaymentMethod(
                this.activeOrder
            );
            if (!paymentResponse.success) {
                this.$toast.open({
                    message: paymentResponse.errors.GLOBAL,
                    type: "error"
                });

                return;
            }

            // Pass to Brain Tree for payment
            this.$router.push({
                path: "/invoices/paymentReceipt/" + paymentResponse.postedPayment.paymentId
            });
        },

        // Handle Save Card toggling
        toggleSaveCardHandler() {

            // Update the native JS save card var that Braintree dropin JS can access
            // to our Vue JS var that we'll keep synced to the checkbox.
            jsDoNotSaveCard = this.doNotSaveCard;

            // Set whether or not we're in guest mode (no saving/deleting)
            let vm = this;
            vm.paymentInstance._model.isGuestCheckout = this.doNotSaveCard;
        },

        // Add an event listener to BrainTree dropin buttons that'll toggle edit mode. This includes the
        // edit/cancel button and the delete payment type button.
        addEditCardsClickEvents() {

            // Toggle Card Edit Mode function
            var vm = this;
            var editCardsToggleHandler = function() {

                let checkbox = document.getElementById('saveCard');
                let enteringEditMode = !checkbox.disabled;

                // Save & Delete is not available to guests, therefore:
                // 1. If we're entering edit mode we can't be in guest mode or it won't actually delete
                // 2. If we're leaving edit mode, revert to how the user was set depending on the Save Card setting (guest is opposite)
                vm.paymentInstance._model.isGuestCheckout = (enteringEditMode ? false : jsDoNotSaveCard);

                // Toggle whether or not the checkbox is disabled
                checkbox.disabled = !checkbox.disabled;
            };

            // Add the event listener to the edit/cancel button to call our toggle edit function
            var elements = document.getElementsByClassName('braintree-heading');
            for (var i = 0; i < elements.length; i++) {
                if (elements[i].getAttribute('data-braintree-id') == 'methods-edit') {
                    elements[i].addEventListener('click', editCardsToggleHandler, true);
                    break;
                }
            }

            // Add the event listener to the delete payment type button to call our toggle edit function
            elements = document.getElementsByClassName('braintree-delete-confirmation__button');
            for (var i = 0; i < elements.length; i++) {
                if (elements[i].getAttribute('data-braintree-id') == 'delete-confirmation__yes') {
                    elements[i].addEventListener('click', editCardsToggleHandler, true);
                    break;
                }
            }
        },

        // Go to previous page
        goBack() {
            this.$router.go(-1);
        }
    }
};
</script>

<style scoped></style>
